import { useCallback, useMemo } from 'react'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { DataType, FormatPreset, IndexType } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { JSONObject } from '~/common/scalars'
import { getIconByColumnType } from '~/common/utils/table'
import { AllowedArrayInnerTypes } from '~/common/utils/table-column'
import { EnumOptionLite } from '~/components/shared/enum-options'
import { TableColumnBadge } from './TableColumnBadge'

interface TableColumnAutocompleteProps {
  columns: TableColumnOption[]
  showIcon?: boolean
  onChange: (column: TableColumnOption | undefined) => void
  selectedColumn?: TableColumnOption | null
  disabled?: boolean
  className?: string
  onBlur?: () => void
  tableId?: string
  showBadge?: boolean
}

// This is the subset of TableColumnOption that is needed for the autocomplete
export type TableColumnOption = {
  id: string
  order: number
  name: string
  type: DataType
  innerType: DataType | null
  indexType: IndexType
  formatPreset?: FormatPreset | null
  formatOptions?: JSONObject | null
  resolvedSourceType: DataType
  resolvedEnumOptions?: string[] | null
  enumOptions?: string[] | null
  resolvedSourceColumn?: { column: { enumSet: { options: EnumOptionLite[] } | null } }
  table: {
    id: string
    name: string
  }
} & {
  lookupColumn: {
    id: string
    name: string
    type: DataType
    innerType: DataType | null
    indexType: IndexType
    formatPreset?: FormatPreset | null
    formatOptions?: JSONObject | null
    enumOptions?: string[] | null
  } | null
}

/**
 * Single select autocomplete component for table columns
 */
export function TableColumnAutocomplete({
  columns,
  showIcon,
  onChange,
  onBlur,
  selectedColumn,
  disabled,
  className,
  tableId,
  showBadge = false,
}: TableColumnAutocompleteProps) {
  const { t } = useTypedTranslation()
  const columnOptions = useMemo(() => {
    return columns.map((column) => {
      const isParentTableColumn = column.table.id !== tableId
      return {
        value: column.id,
        label: column.name,
        startIcon: showIcon
          ? getIconByColumnType(column.type, column.innerType as AllowedArrayInnerTypes)
          : undefined,
        endIcon:
          isParentTableColumn && showBadge ? (
            <TableColumnBadge tableName={column.table.name} />
          ) : undefined,
      }
    })
  }, [columns, showBadge, showIcon, tableId])

  const selectedValue = useMemo(() => {
    if (!selectedColumn) {
      return undefined
    }

    const isParentTableColumn = selectedColumn.table.id !== tableId
    return {
      value: selectedColumn.id,
      label: selectedColumn.name,
      startIcon: showIcon
        ? getIconByColumnType(
            selectedColumn.type,
            selectedColumn.innerType as AllowedArrayInnerTypes,
          )
        : undefined,
      endIcon:
        isParentTableColumn && showBadge ? (
          <TableColumnBadge tableName={selectedColumn.table.name} />
        ) : undefined,
    }
  }, [selectedColumn, showBadge, showIcon, tableId])

  const handleChange = useCallback(
    (columnValue: string | undefined) => {
      if (!disabled) {
        // Always call onChange, even with undefined
        onChange(columnValue ? columns.find((col) => col.id === columnValue) : undefined)
      }
    },
    [columns, disabled, onChange],
  )

  return (
    <Dropdown
      className={className}
      disabled={disabled}
      endAdornment={
        selectedColumn && showBadge && selectedColumn.table.id !== tableId ? (
          <TableColumnBadge tableName={selectedColumn.table.name} />
        ) : undefined
      }
      onBlur={onBlur}
      onChange={handleChange}
      options={columnOptions}
      placeholder={t('filter.autocomplete_select_field')}
      showClearButton={false}
      startAdornment={
        selectedColumn && showIcon
          ? getIconByColumnType(
              selectedColumn.type,
              selectedColumn.innerType as AllowedArrayInnerTypes,
            )
          : undefined
      }
      value={selectedValue?.value}
    />
  )
}
